import React, { useMemo, useCallback } from 'react'
import { shallow } from 'zustand/shallow'
import styled, { useTheme as useStyledTheme } from 'styled-components'
import BigNumber from 'bignumber.js'
import { getDisplayApr, getBoostedDisplayApr } from 'views/Farms/utils'
import { useFarmBoostFactor } from 'state/farmBoost/useFarmBoostFactor'
import history from 'routerHistory'
import { Path } from 'config/constants/paths'
import {
  Flex,
  IconButton,
  useModal,
  useTooltip,
  CalculateIcon,
  Rocket,
  Text,
  Skeleton,
  ArrowForwardIcon,
} from 'vvs-uikit'
import RoiCalculatorModal from 'components/RoiCalculatorModal'
import { useTranslation } from 'contexts/Localization'
import { DeserializedFarm } from 'state/types'
import { useDeserializedFarmFromPid } from 'state/farms/useDeserializedFarms'
import { useDeserializedFarmUserFromPid } from 'state/farms/useDeserializedFarmsUser'
import { useLpTokenPrice } from 'state/farms/hooks'
import { setEditingFarmboost } from 'state/farmBoost/useEditingFarmboost'
import { useUserBoostedFarmsInfos } from 'state/farmBoost/useUserBoostedFarmsInfos'
import { useGetEcoBoostFactor } from 'views/Farms/hooks/useGetEcoBoostFactor'
import { useUserVvsBoost } from 'state/xVvs/hooks'
import { getMaxFarmBoostFactor, getMaxEcoBoostFactor } from 'utils/getMaxBoostFactor'
import isNftStakingSupported, { isEcoFarmSupported } from 'utils/isNftStakingSupported'

const ApyLabelContainer = styled(Flex)`
  gap: 4px;
  cursor: pointer;

  &:hover {
    opacity: 0.5;
  }
`

const BoostContainer = styled(Flex)`
  height: fit-content;
  align-items: center;
  padding-bottom: 2px;
  border-bottom: 1px dotted ${({ theme }) => theme.colors.success};
`

interface BasicButtonProps {
  apr?: number
  vvsApr?: number
  lpApr?: number
}

interface ApyButtonProps extends BasicButtonProps {
  variant: 'text' | 'text-and-button'
  pid: number
  version: number
  lpSymbol: string
  lpLabel?: string
  vvsPrice?: BigNumber
  addLiquidityUrl?: string
  poolWeight: BigNumber
  poolLiquidity: BigNumber
  rewardData?: any[]
  farms?: DeserializedFarm[]
}

const ApyButton: React.FC<ApyButtonProps> = (props) => {
  const {
    farms,
    rewardData,
    variant,
    pid,
    version,
    lpSymbol,
    lpLabel,
    vvsPrice,
    apr,
    vvsApr,
    lpApr,
    addLiquidityUrl,
    poolLiquidity,
  } = props
  const [boostedFarms, isBoostedFarmsLoading] = useUserBoostedFarmsInfos(
    (s) => [s.value.boostedFarmsIds, !s.loaded],
    shallow,
  )
  const { t } = useTranslation()

  const lpPrice = useLpTokenPrice(lpSymbol, version)
  const farm = useDeserializedFarmFromPid(pid, version)
  const { tokenBalance, stakedBalance } = useDeserializedFarmUserFromPid(pid, version)
  const showIcon = variant === 'text-and-button'

  const { boostData, userDataLoaded: boostDataLoaded } = useUserVvsBoost()
  const { stakes: xVVSStakes } = boostData

  const displayApr = getDisplayApr(apr, lpApr)

  const [onPresentApyModal] = useModal(
    <RoiCalculatorModal
      linkLabel={t('Get %lpSymbol%', { lpSymbol: lpLabel })}
      stakingTokenBalance={stakedBalance.plus(tokenBalance)}
      stakingTokenSymbol={lpSymbol}
      stakingTokenPrice={lpPrice.toNumber()}
      earningTokenPrice={vvsPrice.toNumber()}
      apr={apr}
      vvsApr={vvsApr}
      displayApr={displayApr}
      lpApr={lpApr}
      linkHref={addLiquidityUrl}
      isFarm
      poolLiquidity={poolLiquidity}
      rewardData={rewardData}
      farm={farm}
      farms={farms}
    />,
  )

  const isBoosted = useMemo(() => {
    return boostedFarms?.includes(pid) && boostDataLoaded && xVVSStakes?.filter((stake) => stake.active).length > 0
  }, [boostedFarms, pid, boostDataLoaded, xVVSStakes])

  const { isEcoBoosted } = useGetEcoBoostFactor(farm.pid)

  if (isBoostedFarmsLoading) {
    return (
      <Flex gap="4px" alignItems="center">
        <Skeleton height={21} width={32} />
      </Flex>
    )
  }

  if (isBoosted || isEcoBoosted) {
    return <BoostedApyButton farm={farm} showIcon={showIcon} onPresentApyModal={onPresentApyModal} {...props} />
  }

  return <NonBoostedApyButton farm={farm} showIcon={showIcon} onPresentApyModal={onPresentApyModal} {...props} />
}

export default ApyButton

const NonBoostedApyButton: React.FC<
  BasicButtonProps & {
    farm: DeserializedFarm
    showIcon: boolean
    onPresentApyModal: () => void
  }
> = (props) => {
  const { apr, vvsApr, lpApr, farm, showIcon, onPresentApyModal } = props
  const displayApr = getDisplayApr(apr, lpApr)
  const displayBoostedApr = getBoostedDisplayApr(
    vvsApr,
    apr,
    lpApr,
    getMaxFarmBoostFactor(farm.pid),
    getMaxEcoBoostFactor(farm.pid),
  )

  return (
    <Flex gap="4px" alignItems="center">
      <ApyLabelContainer
        alignItems="center"
        onClick={(event) => {
          event.stopPropagation()
          onPresentApyModal()
        }}
      >
        <Text>{displayApr}%</Text>
        {showIcon && (
          <IconButton variant="text" scale="xs">
            <CalculateIcon width="18px" />
          </IconButton>
        )}
      </ApyLabelContainer>
      {!farm.boostFarmExpired && showIcon && isNftStakingSupported(farm.pid) && (
        <BoostApyTooltips farmId={farm.pid} displayMaxApr={displayBoostedApr} />
      )}
    </Flex>
  )
}

const BoostedApyButton: React.FC<
  BasicButtonProps & {
    pid: number
    farm: DeserializedFarm
    showIcon: boolean
    onPresentApyModal: () => void
  }
> = (props) => {
  const { colors } = useStyledTheme()
  const { apr, vvsApr, lpApr, pid, farm, showIcon, onPresentApyModal } = props
  const boostFactorData = useFarmBoostFactor(pid)

  const { data: ecoFarmBoostFactor, isLoading: isEcoBoostFactorLoading } = useGetEcoBoostFactor(farm.pid)

  const maxVVSBoostFactor = getMaxFarmBoostFactor(farm.pid)
  const maxEcoBoostFactor = getMaxEcoBoostFactor(farm.pid)
  const displayMaxBoostedApr = getBoostedDisplayApr(vvsApr, apr, lpApr, maxVVSBoostFactor, maxEcoBoostFactor)
  const isLoading = useMemo(
    () => !boostFactorData.loaded || isEcoBoostFactorLoading,
    [boostFactorData.loaded, isEcoBoostFactorLoading],
  )

  const onClick = useCallback(
    (event) => {
      event.stopPropagation()
      if (isEcoFarmSupported(farm.pid)) {
        onPresentApyModal()
        return
      }
      setEditingFarmboost({ selectedBoostFarmIds: [pid] })
      history.push(Path.BOOST_CALCULATOR)
    },
    [farm.pid, onPresentApyModal, pid],
  )

  return (
    <Flex gap="4px" alignItems="center">
      <ApyLabelContainer alignItems="center" onClick={onClick}>
        {!farm.boostFarmExpired && <Rocket width="12px" color={colors.text} />}
        {isLoading ? (
          <Skeleton height={21} width={32} />
        ) : (
          <Text>{getBoostedDisplayApr(vvsApr, apr, lpApr, boostFactorData.boostFactor, ecoFarmBoostFactor)}%</Text>
        )}
        {showIcon && (
          <IconButton variant="text" scale="xs">
            <CalculateIcon width="18px" />
          </IconButton>
        )}
      </ApyLabelContainer>
      {showIcon &&
        isNftStakingSupported(farm.pid) &&
        !isLoading &&
        (boostFactorData.boostFactor < maxVVSBoostFactor || ecoFarmBoostFactor < maxEcoBoostFactor) &&
        !farm.boostFarmExpired && <BoostApyTooltips farmId={farm.pid} displayMaxApr={displayMaxBoostedApr} />}
    </Flex>
  )
}

const BoostApyTooltips: React.FC<{
  displayMaxApr: string
  farmId?: number
}> = ({ displayMaxApr, farmId }) => {
  const { t } = useTranslation()
  const { colors } = useStyledTheme()

  const { targetRef, tooltip, tooltipVisible } = useTooltip(
    isEcoFarmSupported(farmId)
      ? t('Potential highest Boosted APR (Boosted APR is the sum of base APR plus extra APR boosted by NFTs staking)')
      : t(
          'Potential highest Boosted APR (Boosted APR is the sum of base APR plus extra APR boosted by holding xVVS and NFTs staking)',
        ),
    {
      placement: 'top-end',
    },
  )
  return (
    <>
      <ArrowForwardIcon width="10px" />
      <BoostContainer ref={targetRef} gap="2px">
        <Rocket width="10px" color={colors.success} />
        <Text small color={colors.success}>{`${displayMaxApr}%`}</Text>
        {tooltipVisible && tooltip}
      </BoostContainer>
    </>
  )
}
